import { useEffect, useState } from 'preact/hooks';
import { get } from '../../api';
import type { AgentStatus } from '../../types';
import { LcarsPanel } from '../layout/LcarsPanel';
import { LoadingSpinner } from '../shared/LoadingSpinner';

function getAgentState(agent: AgentStatus): 'active' | 'cooldown' | 'error' {
  if (agent.hoursAgo === null) return 'error';
  if (agent.hoursAgo < 1) return 'active';
  if (agent.hoursAgo < 24) return 'cooldown';
  return 'error';
}

function getRingClass(state: 'active' | 'cooldown' | 'error'): string {
  if (state === 'active') return 'rf-active';
  if (state === 'cooldown') return 'rf-cooldown';
  return 'rf-error';
}

function getBadgeClass(state: 'active' | 'cooldown' | 'error'): string {
  if (state === 'active') return 'badge-canary';
  if (state === 'cooldown') return 'badge-orange';
  return 'badge-red';
}

function getBadgeLabel(state: 'active' | 'cooldown' | 'error', agent: AgentStatus): string {
  if (state === 'active') return 'Active';
  if (state === 'cooldown') return 'Cooldown';
  if (agent.schedule === 'manual') return 'No Cron';
  return 'Error';
}

function getCooldownOffset(agent: AgentStatus): string {
  if (agent.hoursAgo === null || agent.hoursAgo < 1) return '';
  const circumference = 75.4;
  const remaining = Math.max(0, 1 - (agent.hoursAgo / 24));
  return String(circumference * remaining);
}

export function AgentsPanel() {
  const [agents, setAgents] = useState<AgentStatus[] | null>(null);

  useEffect(() => {
    get<AgentStatus[]>('/api/agents/status').then(setAgents).catch(console.error);
  }, []);

  return (
    <LcarsPanel
      color="var(--canary)"
      title="Agents"
      action={{ label: 'Run All', onClick: () => {} }}
    >
      {!agents ? (
        <LoadingSpinner />
      ) : (
        agents.map((agent) => {
          const state = getAgentState(agent);
          const ringClass = getRingClass(state);
          const hoursLabel = agent.hoursAgo !== null ? `${Math.round(agent.hoursAgo)}h` : '!';

          return (
            <div class="agent-row" key={agent.key}>
              <div class="ring-wrap">
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <circle class="ring-bg" cx="16" cy="16" r="12" />
                  {state === 'active' ? (
                    <circle
                      class={`ring-fill ${ringClass}`}
                      cx="16" cy="16" r="12"
                      stroke-dasharray="6 3"
                    />
                  ) : state === 'cooldown' ? (
                    <circle
                      class={`ring-fill ${ringClass}`}
                      cx="16" cy="16" r="12"
                      stroke-dasharray="75.4"
                      stroke-dashoffset={getCooldownOffset(agent)}
                    />
                  ) : (
                    <circle
                      class={`ring-fill ${ringClass}`}
                      cx="16" cy="16" r="12"
                      stroke-dasharray="5 3"
                    />
                  )}
                </svg>
                <div
                  class="ring-center"
                  style={{
                    color: state === 'active' ? 'var(--canary)' : state === 'cooldown' ? 'var(--orange)' : 'var(--red)',
                    fontSize: state === 'error' ? '14px' : '7px',
                    fontFamily: state !== 'error' ? 'var(--mono)' : undefined,
                  }}
                >
                  {state === 'active' ? '↺' : state === 'error' ? '!' : hoursLabel}
                </div>
              </div>
              <div class="agent-info">
                <div class="agent-name">{agent.name}</div>
                <div class="agent-meta">
                  {agent.scheduleDesc} · {agent.hoursAgo !== null ? `ran ${agent.hoursAgo < 1 ? '<1h' : Math.round(agent.hoursAgo) + 'h'} ago` : 'no runs'}
                </div>
              </div>
              <div class="agent-right">
                <span class={`lcars-badge ${getBadgeClass(state)}`}>{getBadgeLabel(state, agent)}</span>
                <span class="agent-output" style={state === 'error' ? { color: 'var(--red)' } : undefined}>
                  {agent.output || '—'}
                </span>
              </div>
              <button class="trigger-btn">Run</button>
            </div>
          );
        })
      )}
    </LcarsPanel>
  );
}
